import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import MenuIcon from "@mui/icons-material/Menu";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import { navigation } from "../../app-navigation";
import { useAuth } from "../../contexts/auth";
import { colors } from "../../pages";
import { useThemeMode } from "../../contexts/theme-mode";
import routes from "../../routes/app-routes";

const ICON_SIZE = 32;

const FALLBACK_ICON_COMPONENTS = {
  home: HomeOutlinedIcon,
  group: PeopleOutlineIcon,
  folder: FolderOutlinedIcon,
  fill: ScienceOutlinedIcon,
  pointOfSale: PointOfSaleOutlinedIcon,
};

function simpleCopy(item) {
  return JSON.parse(JSON.stringify(item));
}

function isImageIcon(icon) {
  return typeof icon === "string" && (icon.includes("/") || icon.includes("."));
}

function isNavPathActive(currentPath, itemPath) {
  if (!itemPath) return false;
  const pathOnly = itemPath.split("?")[0];
  return currentPath === pathOnly || currentPath.startsWith(`${pathOnly}/`);
}

function NavIcon({ icon }) {
  if (!icon) return null;

  if (isImageIcon(icon)) {
    return (
      <Box
        component="img"
        src={icon}
        alt=""
        sx={{
          width: ICON_SIZE,
          height: ICON_SIZE,
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  }

  const IconComponent = FALLBACK_ICON_COMPONENTS[icon];
  if (!IconComponent) return null;

  return <IconComponent sx={{ fontSize: ICON_SIZE, color: colors.gold }} />;
}

export default function SideNavigationMenu({
  onNavigate,
  openMenu,
  compactMode,
  onMenuToggle,
  showMenuToggle = true,
}) {
  const { user } = useAuth();
  const { isDark } = useThemeMode();
  const sidebarBg = isDark ? colors.charcoal : colors.black;
  const location = useLocation();
  const currentPath = location.pathname;

  const filterNavigation = useCallback(
    (navItems, parentNode) => {
      let hiddenNodeCount = 0;
      for (let i = 0; i < navItems.length; i++) {
        const nav = navItems[i];
        if (nav.path) {
          const matchingRoute = routes.find((route) => route.path === nav.path);
          if (matchingRoute) {
            const routeUserTypes = matchingRoute.allowedUserTypes || [];
            const currUserType = user?.userRoles || [];

            if (!routeUserTypes.some((role) => currUserType.includes(role))) {
              nav.visible = false;
              hiddenNodeCount++;
              if (parentNode && navItems.length === hiddenNodeCount) {
                parentNode.visible = false;
              }
            }
          }
        } else if (nav.items) {
          nav.items = filterNavigation(nav.items, nav);
        }
      }
      return navItems;
    },
    [user]
  );

  const items = useMemo(() => {
    const newNavigation = filterNavigation(simpleCopy(navigation));
    return newNavigation
      .filter((item) => item.visible !== false)
      .map((item) => {
        if (item.path && !/^\//.test(item.path)) {
          item.path = `/${item.path}`;
        }
        if (item.items) {
          item.items = item.items.filter((child) => child.visible !== false);
        }
        return item;
      });
  }, [filterNavigation]);

  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (compactMode) {
      setExpanded({});
      return;
    }

    const next = {};
    items.forEach((item) => {
      if (!item.items?.length) return;
      const childActive = item.items.some((child) =>
        isNavPathActive(currentPath, child.path)
      );
      if (childActive) {
        next[item.text] = true;
      }
    });
    setExpanded(next);
  }, [compactMode, currentPath, items]);

  const handleNavigate = useCallback(
    (path) => {
      if (!path || !onNavigate) return;
      onNavigate(path);
    },
    [onNavigate]
  );

  const toggleGroup = (text) => {
    if (compactMode && openMenu) {
      openMenu();
      return;
    }
    setExpanded((prev) => ({ ...prev, [text]: !prev[text] }));
  };

  const renderItemButton = (item, { nested = false } = {}) => {
    const selected = Boolean(item.path && currentPath === item.path);
    const hasChildren = Boolean(item.items?.length);
    const isOpen = Boolean(expanded[item.text]);
    const accentBg = item.bgColor ? colors[item.bgColor] : null;
    const rowBg = accentBg || sidebarBg;

    return (
      <ListItemButton
        key={item.path || item.text}
        selected={selected}
        onClick={() => {
          if (compactMode && openMenu) {
            openMenu();
            return;
          }
          if (hasChildren) {
            toggleGroup(item.text);
            return;
          }
          handleNavigate(item.path);
        }}
        sx={{
          minHeight: nested ? 40 : 52,
          px: compactMode ? 0 : 1.5,
          py: 1,
          pl: nested ? 7 : compactMode ? 0 : 1.5,
          justifyContent: compactMode ? "center" : "flex-start",
          color: nested ? colors.muted : colors.white,
          bgcolor: rowBg,
          borderTop: nested
            ? "none"
            : `1px solid ${colors.divider}`,
          borderBottom: "none",
          "&.Mui-selected": {
            bgcolor: accentBg || "rgba(197, 160, 111, 0.12)",
            color: nested ? colors.muted : colors.white,
            "&:hover": {
              bgcolor: accentBg || "rgba(197, 160, 111, 0.18)",
              filter: accentBg ? "brightness(1.08)" : "none",
            },
          },
          "&:hover": {
            bgcolor: accentBg || "rgba(255, 255, 255, 0.06)",
            filter: accentBg ? "brightness(1.08)" : "none",
          },
        }}
      >
        {!nested && item.icon && (
          <ListItemIcon
            sx={{
              minWidth: compactMode ? 0 : ICON_SIZE + 16,
              color: colors.gold,
              justifyContent: "center",
            }}
          >
            <NavIcon icon={item.icon} />
          </ListItemIcon>
        )}
        {!compactMode && (
          <ListItemText
            primary={item.text}
            primaryTypographyProps={{
              fontSize: nested ? "0.85rem" : "0.95rem",
              fontWeight: nested ? 400 : 600,
              color: "inherit",
              noWrap: true,
            }}
          />
        )}
        {!compactMode &&
          hasChildren &&
          (isOpen ? (
            <ExpandLessIcon sx={{ color: colors.white, fontSize: 20 }} />
          ) : (
            <ExpandMoreIcon sx={{ color: colors.white, fontSize: 20 }} />
          ))}
      </ListItemButton>
    );
  };

  return (
    <Box
      onClick={() => {
        if (compactMode && openMenu) openMenu();
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        bgcolor: sidebarBg,
        color: colors.white,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: compactMode ? "center" : "flex-start",
          gap: 1,
          px: compactMode ? 0.5 : 1,
          py: 1,
          minHeight: 56,
          borderBottom: "none",
          bgcolor: sidebarBg,
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showMenuToggle && onMenuToggle && (
          <IconButton
            onClick={onMenuToggle}
            sx={{ color: colors.white }}
            aria-label="Toggle menu"
          >
            <MenuIcon />
          </IconButton>
        )}
        {!compactMode && (
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL || ""}/assets/logo.png`}
            alt="QUE"
            sx={{
              height: 36,
              width: "auto",
              maxWidth: 120,
              objectFit: "contain",
              display: "block",
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          bgcolor: sidebarBg,
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <List disablePadding>
          {items.map((item) => {
            if (item.items?.length) {
              const isOpen = Boolean(expanded[item.text]) && !compactMode;
              return (
                <Box key={item.text}>
                  {renderItemButton(item)}
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.items.map((child) =>
                        renderItemButton(child, { nested: true })
                      )}
                    </List>
                  </Collapse>
                </Box>
              );
            }
            return renderItemButton(item);
          })}
        </List>
      </Box>
    </Box>
  );
}
